import { useState, useEffect } from 'react';

/**
 * Custom hook to detect device type and orientation
 * @returns {Object} Device detection state
 */
export function useDeviceDetection() {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    orientation: 'landscape', // 'portrait' or 'landscape'
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  });

  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Detect mobile devices by user agent
      const isMobileUA = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent.toLowerCase()
      );
      
      // Detect tablets separately (including iPad)
      const isTabletUA = /ipad|android(?!.*mobile)/i.test(userAgent.toLowerCase()) || 
                         (isMobileUA && width >= 768);
      
      // Mobile detection: narrow screens OR phone user agent
      // Tablets go to desktop view (width >= 768)
      const isMobile = (width < 768) || (isMobileUA && !isTabletUA && width < 1024);
      
      // Tablet detection
      const isTablet = isTabletUA || (!isMobileUA && width >= 768 && width <= 1024);
      
      // Desktop detection
      const isDesktop = !isMobile && !isTablet;
      
      // Orientation detection
      const orientation = height > width ? 'portrait' : 'landscape';

      console.log('Device Detection:', { isMobile, isTablet, isDesktop, width, height, userAgent: userAgent.substring(0, 50) });

      setDeviceInfo({
        isMobile,
        isTablet,
        isDesktop,
        orientation,
        screenWidth: width,
        screenHeight: height,
      });
    };

    // Initial detection
    detectDevice();

    // Listen for resize and orientation change events
    const handleResize = () => detectDevice();
    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated
      setTimeout(detectDevice, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Also listen to matchMedia for orientation
    const mediaQuery = window.matchMedia('(orientation: portrait)');
    const handleMediaChange = () => detectDevice();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleMediaChange);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      } else {
        mediaQuery.removeListener(handleMediaChange);
      }
    };
  }, []);

  return deviceInfo;
}
